/**
 * Inspection type catalog for the Fleet Hub booking modal. Mirrors the HDP B2C
 * widget so both B2C and B2B bookings hit the same HubSpot `ordertype` values.
 *
 * Icons use Material Icons to match BookInspectionModal.tsx's existing icon
 * imports (`react-native-vector-icons/MaterialIcons`).
 */

import type { InspectionType } from "@/services/firebase/functions"

export type { InspectionType }

export interface InspectionService {
  id: InspectionType
  name: string // Full display name
  shortName: string // Short chip label — must match HubSpot `ordertype` internal value
  chipTitle?: string // Title override for primary chips (defaults to shortName)
  chipLabel?: string // Tiny secondary label shown under chip title (e.g. pricing tier)
  iconName: string // Material Icons name
  badgeText?: string // Optional small badge above chip (e.g. "Standard", "Kostenlos")
  badgeVariant?: "standard" | "free" // Color variant for the badge
  isPrimary: boolean // Primary row vs "Andere" expander
  sortOrder: number
}

/**
 * Catalog shown in the Fleet Hub inspection-type picker. Primary row shows
 * HU/AU (default), Nur HU, Nachkontrolle — the three most common B2B orders.
 * The "Andere" expander reveals the remaining four services for edge cases.
 */
export const INSPECTION_SERVICES: InspectionService[] = [
  {
    id: "hu_au",
    name: "Hauptuntersuchung + Abgasuntersuchung",
    shortName: "HU/AU",
    chipTitle: "HU/AU",
    iconName: "verified-user",
    badgeText: "Standard",
    badgeVariant: "standard",
    isPrimary: true,
    sortOrder: 0,
  },
  {
    id: "hu",
    name: "Hauptuntersuchung",
    shortName: "Nur HU",
    chipTitle: "Nur HU",
    iconName: "visibility",
    isPrimary: true,
    sortOrder: 1,
  },
  {
    id: "nachkontrolle",
    name: "Nachkontrolle",
    shortName: "Nachkontrolle",
    chipTitle: "Nachkontrolle",
    iconName: "replay",
    isPrimary: true,
    sortOrder: 2,
  },
  {
    id: "unfallgutachten",
    name: "Unfallgutachten",
    shortName: "Unfallgutachten",
    iconName: "report-problem",
    isPrimary: false,
    sortOrder: 3,
  },
  {
    id: "vollgutachten_21",
    name: "Vollgutachten §21 StVZO",
    shortName: "Vollgutachten §21",
    iconName: "description",
    isPrimary: false,
    sortOrder: 4,
  },
  {
    id: "eintragung_19",
    name: "Eintragung §19 StVZO",
    shortName: "Eintragung §19",
    iconName: "assignment",
    isPrimary: false,
    sortOrder: 5,
  },
  // Frühjahrscheck intentionally excluded — B2B Fleet Hub customers cannot
  // book it (B2C-only product). It remains in the InspectionType union
  // because OrbitControl ops and the backend still accept it for other channels.
]

export const DEFAULT_INSPECTION_TYPE: InspectionType = "hu_au"

export function getInspectionService(id: InspectionType): InspectionService {
  const svc = INSPECTION_SERVICES.find((s) => s.id === id)
  if (!svc) throw new Error(`Unknown inspection type: ${id}`)
  return svc
}
